import {
  Box,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import { useLocalization } from '@/hooks/useLocalization';
import { useEmbeddableSelectMenuProps } from '@/integrations/useEmbeddableSelectMenuProps';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

/** A non-grouping action rendered at the bottom of the dropdown (e.g. Manage Groups). */
export interface GroupBySelectAction {
  key: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
}

/**
 * Generic interface for group options.
 * Components using GroupBySelect should provide options conforming to this shape.
 */
export interface GroupOption {
  value: string;
  label: string;
}

export interface GroupBySelectProps<T extends GroupOption> {
  /** The label for the select input */
  label: string;
  /** Array of available group options */
  options: T[];
  /** The currently active/selected option */
  activeOption: T;
  /** Callback when a new option is selected */
  onOptionChange: (option: T) => void;
  /** The user's saved default group value (from server) */
  savedGroupValue?: string;
  /** The firm's default group value (from menu settings) */
  firmDefaultGroup?: string;
  /** Whether the current selection differs from the saved/firm default */
  hasUnsavedGroupChange: boolean;
  /** Whether the user is currently using the firm default (no user override saved) */
  isUsingFirmDefaultGroup: boolean;
  /** Callback to save the current selection as user's default */
  onSaveAsDefault: () => void;
  /** Callback to reset to the firm default */
  onResetToFirmDefault: () => void;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Minimum width for the FormControl (default: 120) */
  minWidth?: number;
  /** Custom sx prop for FormControl */
  sx?: SxProps<Theme>;
  /** Optional actions rendered below the options (e.g. "Manage Groups"). */
  extraActions?: GroupBySelectAction[];
}

/** Merge a base `sx` with the caller's optional `sx` (single value or array). */
function mergeSx(baseSx: SxProps<Theme>, sx?: SxProps<Theme>): SxProps<Theme> {
  if (!sx) return baseSx;
  const sxItems: SxProps<Theme>[] = Array.isArray(sx)
    ? (sx as SxProps<Theme>[])
    : [sx];
  return [baseSx, ...sxItems] as SxProps<Theme>;
}

/**
 * A reusable Select component for group-by functionality with:
 * - Default chip indicator for user/firm defaults
 * - Save as default button for unsaved changes
 * - Reset to firm default option
 *
 * Used in PortfolioAccounts, PortfolioHoldings, and GainLoss views.
 */
export function GroupBySelect<T extends GroupOption>({
  label,
  options,
  activeOption,
  onOptionChange,
  savedGroupValue,
  firmDefaultGroup,
  hasUnsavedGroupChange,
  isUsingFirmDefaultGroup,
  onSaveAsDefault,
  onResetToFirmDefault,
  disabled = false,
  minWidth = 120,
  sx,
  extraActions,
}: GroupBySelectProps<T>) {
  const { t } = useLocalization();
  const embeddableMenuProps = useEmbeddableSelectMenuProps();

  const formControlSx = mergeSx({ minWidth }, sx);

  const handleChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value;
    const selectedOption = options.find((option) => option.value === newValue);
    if (selectedOption) {
      onOptionChange(selectedOption);
    }
  };

  return (
    <FormControl
      size='small'
      sx={formControlSx}
      disabled={disabled}
    >
      <InputLabel id='group-by-select-label'>{label}</InputLabel>
      <Select
        labelId='group-by-select-label'
        id='group-by-select'
        value={activeOption.value}
        label={label}
        onChange={handleChange}
        renderValue={() => activeOption.label}
        MenuProps={embeddableMenuProps}
      >
        {options.map((option) => {
          const isSelected = option.value === activeOption.value;
          const isUserDefault = option.value === savedGroupValue;
          const isFirmDefault = option.value === firmDefaultGroup;
          // Show "Default" chip if: user saved this as default, OR (no user saved value AND this is the firm default AND it's selected)
          const noUserSavedValue = !savedGroupValue;
          const showDefaultChip =
            isUserDefault || (noUserSavedValue && isFirmDefault && isSelected);
          // Show save icon only if selected, has unsaved change, and NOT showing the default chip
          const showSaveIcon =
            isSelected && hasUnsavedGroupChange && !showDefaultChip;

          return (
            <MenuItem
              key={option.value}
              value={option.value}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                {option.label}
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                  {showDefaultChip && (
                    <Chip
                      size='small'
                      label={t('default', 'Default')}
                      sx={{ height: 20 }}
                    />
                  )}
                  {showSaveIcon && (
                    <Tooltip title={t('saveAsDefault', 'Save as default')}>
                      <IconButton
                        size='small'
                        onClick={(e) => {
                          e.stopPropagation();
                          onSaveAsDefault();
                        }}
                        sx={{
                          ml: 0.5,
                          p: 0.25,
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                          border: 'none',
                          '&:hover': {
                            backgroundColor: 'transparent',
                          },
                        }}
                      >
                        <SaveIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </MenuItem>
          );
        })}
        {extraActions?.map((action) => (
          <MenuItem
            key={action.key}
            onClick={(e) => {
              e.stopPropagation();
              action.onClick();
            }}
            sx={{
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            {action.icon ? <ListItemIcon>{action.icon}</ListItemIcon> : null}
            {action.label}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            onResetToFirmDefault();
          }}
          disabled={isUsingFirmDefaultGroup}
        >
          <ListItemIcon>
            <RestoreIcon fontSize='small' />
          </ListItemIcon>
          {t('resetToFirmDefault', 'Reset to firm default')}
        </MenuItem>
      </Select>
    </FormControl>
  );
}

export default GroupBySelect;
